import SettingsIcon from "@mui/icons-material/Settings";
import {
  ClickAwayListener,
  Divider,
  IconButton,
  Paper,
  Popper,
} from "@mui/material";
import { Button, Spacer, Toolbar } from "common";
import I18n from "i18n-js";
import { useEffect, useRef, useState } from "react";
import FieldEditor from "./FieldEditor";
import { FieldCoordinates, useFormEditor } from "./FormEditorDataProvider";

export interface FieldMenuProps {
  coordinates: FieldCoordinates;
}

// Keep only one popper open at a time (shared singleton)
let activeMenu: (() => void) | null = null;

export default function FieldMenu({ coordinates }: FieldMenuProps) {
  const { handleDeleteField, getField } = useFormEditor();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const popperRef = useRef<any>(null);
  const field = getField(coordinates);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // close any other open menu
    if (activeMenu && activeMenu !== handleClose) activeMenu();
    activeMenu = handleClose;

    setAnchorEl(prev => (prev ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (activeMenu === handleClose) activeMenu = null;
  };

  // ✅ Auto-refresh Popper position when content resizes
  useEffect(() => {
    if (!popperRef.current) return;
    const observer = new ResizeObserver(() => {
      popperRef.current?.update?.();
    });
    const paper = popperRef.current.querySelector(".MuiPaper-root");
    if (paper) observer.observe(paper);
    return () => observer.disconnect();
  }, [open]);

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <SettingsIcon fontSize="small" />
      </IconButton>

      <Popper
        ref={popperRef}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        modifiers={[
          { name: "computeStyles", options: { adaptive: true } },
          { name: "eventListeners", options: { scroll: true, resize: true } },
        ]}
      >
        {open && (
          <ClickAwayListener onClickAway={handleClose}>
            <Paper elevation={3}>
              {!!field && (
                <FieldEditor field={field} coordinates={coordinates} />
              )}
              <Divider />
              <Toolbar sx={{ padding: 1 }}>
                <Spacer right />
                <Button
                  variant="text"
                  color="error"
                  title={I18n.t("FormEditor.removeField")}
                  onClick={() => handleDeleteField(coordinates)}
                />
              </Toolbar>
            </Paper>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
